<template>
  <div class="container">
    <MdDialog :md-active.sync="showDialog" v-if="exists && !isHost" :md-fullscreen="false" :md-close-on-esc="false">
      <MdDialogTitle>Welcome</MdDialogTitle>
      <MdDialogContent>
        Welcome to the virtual showing!
      </MdDialogContent>
      <MdDialogActions>
        <MdButton @click="showDialog = false">Continue</MdButton>
      </MdDialogActions>
    </MdDialog>
    <MdApp>
      <MdAppToolbar class="md-primary md-dense" md-elevation="1">
        <div class="md-toolbar-section-start" style="flex-grow: 10;overflow: hidden;">
          <h3 class="md-title title" v-if="title">{{ title }}</h3>
          <h3 class="md-title" v-if="!title">
            <img src="/images/iguide-logo.svg" class="logo" alt="iGUIDE" />
            <span class="title">Virtual Showing</span>
          </h3>
        </div>

        <div class="md-toolbar-section-end" v-if="exists">
          <ShowClients :user="this.$root.$data.user" :clients="clients" />
          <ShareLinkBtn :url="shareUrl" :title="title" />
        </div>
      </MdAppToolbar>

      <MdAppContent class="md-layout md-alignment-center-center"
        v-bind:class="{'debug-mode': debugMode, 'view-mode': !debugMode}">
        <div v-if="!exists">
          The virtual showing could not be found. It either expired or the URL is incorrect.
          <br /><br />
          <RouterLink to="/create">
            <MdButton class="md-raised md-primary">
              Create a Virtual Showing
            </MdButton>
          </RouterLink>
        </div>
        <div class="viewer-container" v-if="exists">
          <div id="fullscreen-button" v-if="fullscreenEnabled" @click="toggleFullscreen">
            <i class="material-icons">{{isFullscreen ? 'fullscreen_exit' : 'fullscreen'}}</i>
          </div>
          <div class="overlay" v-if="!isHost"
            v-bind:class="{'waiting': !isHostConnected && !hostHasLeft, 'hostleft' : !isHostConnected && hostHasLeft}">
            <div class="waiting-for-host">Waiting for host...</div>
            <div class="host-has-left">
              <a href="https://goiguide.com" target="_blank">
                The host has left.<br />
                Click here to learn more about
                <img src="/images/iguide-logo.svg" alt="iGUIDE" />
              </a>
            </div>
          </div>
          <MdProgressSpinner v-if="!iguideUrl" md-mode="indeterminate" style="margin-top: 50px;"></MdProgressSpinner>
          <Viewer
            v-if="iguideUrl"
            :url="iguideUrl"
            :isHost="isHost"
            @event="processViewerEvent"
            @ready="processViewerReady"
            @landscape="processLandscapeEvent"
            @title="setTitle"
          />
        </div>

        <MdDivider v-if="debugMode" />

        <DebugPanel :commands="commands" @clear="clearCommands" v-if="debugMode" />
      </MdAppContent>
    </MdApp>
  </div>
</template>

<script>
import DebugPanel from '../components/DebugPanel';
import ShareLinkBtn from '../components/ShareLinkBtn';
import ShowClients from '../components/ShowClients';
import Viewer from '../components/Viewer.vue';
import { db, ServerValue, analytics } from '../firebase';
import { ref, onValue, push, set, get, onDisconnect, onChildAdded, query, orderByKey } from 'firebase/database';
import { logEvent } from 'firebase/analytics';
import NoSleep from 'nosleep.js';

export default {
  components: {
    DebugPanel,
    ShareLinkBtn,
    ShowClients,
    Viewer,
  },

  props: {
    showingId: { type: String, required: true },
  },

  data() {
    return {
      loading: true,
      debugMode: this.$root.$route.query.debug !== undefined,
      shareUrl: '',
      iguideUrl: '',
      title: '',
      hostUid: '',
      hostLeft: false,
      clients: [],
      commands: [],
      fullscreenEnabled: false,
      isFullscreen: false,
      hasClientsChanged: false,
      showDialog: true,
      exists: true,
      existingMessageCount: 0,
      messageCount: 0,
      lastMessage: {},
    };
  },

  computed: {
    isHost() {
      return this.$root.$data.user.uid === this.hostUid;
    },
    isHostConnected() {
      return this.clients.some(c => c.host);
    },
    hostHasLeft() {
      return this.hostLeft;
    },
  },

  beforeMount() {
    const { user } = this.$root.$data;

    this.fullscreenEnabled = document.fullscreenEnabled;

    this.disconnectCbs = [];
    this.shareUrl = window.location.href;


    const noSleep = new NoSleep();
    const enableNoSleep = () => {
      noSleep.enable();
      document.removeEventListener('click', enableNoSleep);
    };
    document.addEventListener('click', enableNoSleep);

    const showingRef = ref(db, `showings/${this.showingId}`);
    get(showingRef).then(s => {
      const v = s.val();
      if (!v) {
        this.exists = false;
        noSleep.disable();
        document.removeEventListener('click', enableNoSleep);
        return;
      }

      this.iguideUrl = v.iguideUrl;
      this.hostUid = v.hostUid;
      this.hostLeft = v.hostLeft;

      // Watch all connected clients
      const clientRef = ref(db, `clients/${this.showingId}`);
      const onClientsChanged = s => {
        this.clients = s.val() ? Object.entries(s.val()).map(e => ({
          ...e[1],
          uid: e[0],
          self: e[0] === user.uid,
          host: e[0] === this.hostUid,
        })) : [];
        // onClientsChanged() must run once before we can determine if the host has left
        this.hostLeft = this.hasClientsChanged && !this.clients.some(c => c.host);
        this.hasClientsChanged = true;
      };
      const unsubscribeClients = onValue(clientRef, onClientsChanged);
      this.disconnectCbs.push(() => unsubscribeClients());

      // Watch all messages
      const messageRef = ref(db, `messages/${this.showingId}`);
     const onNewMessage = s => {
        if (user.uid !== this.hostUid) {
          this.messageCount++;
          if (this.messageCount > this.existingMessageCount) {
            this.$root.$emit('action', s.val());
          } else {
  switch (s.val().event) {
            case 'tour:move-start':
              this.lastMessage.move = s.val();
              this.lastMessage.move.args[2] = 'crossfade';
              this.lastMessage.last = 'move';
              break;
            case 'tour:camera-move':
              this.lastMessage.camera = s.val();
              this.lastMessage.last = 'camera';
              break;
            case 'tour:floorplan-resize':
              this.lastMessage.floorplan = s.val().args[0];
              break;
            case 'tour:landscape':
              this.lastMessage.landscape = s.val().args[0];
              break;
            default:
              break;
            }
            if (this.messageCount === this.existingMessageCount && this.lastMessage.last) {
              this.lastMessage.complete = true;
              this.$emit('messages-synced');
            }
          }
        }
        this.commands.unshift({ key: s.key, ...s.val() });
      };
      get(messageRef).then(s => {
        return s.size;
      }).then(count => {
        this.existingMessageCount = count;
        const orderedQuery = query(ref(db, `messages/${this.showingId}`), orderByKey());
        const unsubscribe = onChildAdded(orderedQuery, onNewMessage);
        this.disconnectCbs.push(() => unsubscribe());

        // If the host is connecting
        if (user.uid === this.hostUid) {
          // Add `disconnect` message to indicate the last time a showing was used
          const disconnectRef = ref(db, `messages/${this.showingId}/disconnect`);
          onDisconnect(disconnectRef).set({
            event: 'host-disconnect',
            args: false,
            uid: user.uid,
            timestamp: ServerValue.TIMESTAMP,
          });
          window.onbeforeunload = () => { return 'You are the host. Are you sure you want to leave?'};
        }

        // Mark the current user as connected
        const connStateRef = ref(db, `clients/${this.showingId}/${user.uid}`);
        set(connStateRef, { displayName: user.displayName }).then(() => {
          logEvent(analytics, 'showing_connected', { id: connStateRef.key, iguide: this.iguideUrl });
        });
        onDisconnect(connStateRef).remove(() => {
          logEvent(analytics, 'showing_disconnected', { id: connStateRef.key, iguide: this.iguideUrl });
        });
      });
    });

    // Catch the user pressing the Escape button on desktop or Back button on Android
    document.onfullscreenchange = () => {
      if (!document.fullscreenElement) {
        this.isFullscreen = false;
      }
    };
  },

  beforeDestroy() {
    for (const cb in this.disconnectCbs) {
      cb();
    }
    this.disconnectCbs = [];
  },

  methods: {
    processViewerEvent(data) {
      const { user } = this.$root.$data;
      if (user.uid === this.hostUid) {
        data.uid = user.uid;
        data.timestamp = ServerValue.TIMESTAMP;
        const messageRef = ref(db, `messages/${this.showingId}`);
        push(messageRef, data);
      }
    },

    processViewerReady() {
      const syncLastMessage = () => {
        this.$root.$emit('sync-start', this.lastMessage);
      };

      if (this.lastMessage.complete) {
        syncLastMessage();
      } else {
        this.$root.$once('messages-synced', syncLastMessage);
      }
    },

    processLandscapeEvent(isLandscape) {
      const { user } = this.$root.$data;
      if (user.uid === this.hostUid) {
        // send landscape message
        let data = {
          event: 'tour:landscape',
          args: [isLandscape],
          uid: user.uid,
          timestamp: ServerValue.TIMESTAMP,
        };
        const messageRef = ref(db, `messages/${this.showingId}`);
        push(messageRef, data);
        if (isLandscape) {
          // if landscape, also send floorplan-resize 'max' message to sync all clients' floorplans
          data = {
            event: 'tour:floorplan-resize',
            args: ['max'],
            uid: user.uid,
            timestamp: ServerValue.TIMESTAMP,
          };
          push(messageRef, data);
        }
      }
    },

    setTitle(title) {
      this.title = title;
    },

    clearCommands() {
      const messageRef = ref(db, `messages/${this.showingId}`);
      onDisconnect(messageRef).remove();
      this.commands = [];
    },

    toggleFullscreen() {
      if (document.fullscreenElement) {
        document.exitFullscreen().then(() => {
          this.isFullscreen = false;
        });
        return;
      }

      const container = document.body;
      if (container) {
        container.requestFullscreen({ navigationUI: "hide" }).then(() => {
          this.isFullscreen = true;
        });
      }
    },
  },
};
</script>

<style>
  .viewer-container {
    width: 100%;
  }

  .view-mode {
    padding: 0;
    border: 0;
  }
  .view-mode .viewer-container {
    height: 100%;
  }
  .view-mode .viewer-container .overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  .view-mode .viewer-container iframe {
    height: 100%;
  }

  .debug-mode .viewer-container .overlay {
    display: none;
  }
  .debug-mode .viewer-container iframe {
    min-height: 60vh;
  }

  .waiting-for-host,
  .host-has-left {
    display: none;
    position: relative;
    justify-content: center;
    top: 45%;
    background-color: rgba(0, 0, 0, 0.6);
    color: white;
    user-select: none;
  }

  .waiting-for-host {
    font-size: 2em;
    line-height: 2em;
    pointer-events: none;
  }

  .host-has-left {
    font-size: 1.5em;
    line-height: 1.5em;
  }
  .host-has-left a {
    display: block;
  }
  .host-has-left a:hover {
    text-decoration: none;
  }
  .host-has-left img {
    height: 1em;
    margin: 0 3px 4px 3px;
  }

  .overlay.waiting .waiting-for-host,
  .overlay.hostleft .host-has-left {
    display: block;
  }

  #fullscreen-button {
    position: absolute;
    right: 0;
    bottom: 0;
    cursor: pointer;
    height: 50px;
    z-index: 1;
    user-select: none;
  }
  #fullscreen-button i {
    color: white;
    font-size: 50px;
    text-shadow: 3px 2px 2px rgba(0, 0, 0, 0.5);
  }

  .md-divider {
    width: 100%;
    margin: 10px 0;
  }

  .md-title a:hover {
    text-decoration: none;
  }
  .md-title .logo {
    height: 1em;
    margin: 0 5px 5px 0;
  }
  .md-title .title {
    font-weight: 500;
    vertical-align: top;
    user-select: none;
  }
  h3.md-title.title {
    font-weight: 500;
  }

  .md-app-container {
    transform: none;
  }

  .container {
    width: 100%;
    height: 100%;
  }
</style>
